/** @format */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService } from '@funidata/ngx-fudis';
import { toJson } from 'angular';
import { LocalId, OtmId } from 'common-typescript/types';
import { Observable, shareReplay } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { mono, requestQueueIgnoreConcurrent } from 'sis-common';
import { AlertsService, AppErrorHandler, handleSubmission, SisFormBuilder } from 'sis-components';
import { TimingActionService } from '../service/timing-action.service';
import { StudyPeriodData } from '../timing.types';
import { SelectableStudiesService } from './selectable-studies.service';
import { EducationPhaseStudies, isSelectableCourseUnit, isSelectableCustomStudyDraft, SelectableStudy } from './types';

export interface SelectCourseUnitsModalValues {
    studyPeriodData: StudyPeriodData;
}

export function selectCourseUnitsModalOpener(): (
    data: SelectCourseUnitsModalValues,
    viewContainerRef: ViewContainerRef,
) => MatDialogRef<SelectCourseUnitsModalComponent, null> {
    const fudisDialogService = inject(FudisDialogService);
    return (data, viewContainerRef) =>
        fudisDialogService.open<SelectCourseUnitsModalComponent, null, SelectCourseUnitsModalValues>(SelectCourseUnitsModalComponent, {
            data,
            viewContainerRef,
        });
}

interface SelectStudiesForm {
    selectedStudies: FormArray<FormControl<SelectableStudy[] | null>>;
}

@Component({
    selector: 'app-select-course-units-modal',
    standalone: false,
    templateUrl: './select-course-units-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SelectableStudiesService],
})
export class SelectCourseUnitsModalComponent {
    readonly data: SelectCourseUnitsModalValues = inject(MAT_DIALOG_DATA);
    private readonly fb: SisFormBuilder = inject(SisFormBuilder);
    private readonly alertsService = inject(AlertsService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly timingActionService = inject(TimingActionService);
    private readonly fudisDialogService = inject(FudisDialogService);
    private readonly selectableStudiesService = inject(SelectableStudiesService);
    private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    private readonly saveQueue = requestQueueIgnoreConcurrent<void, void>(
        () => mono(this.saveSelectedStudies()),
        () => this.onSuccess(),
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(),
    );

    private readonly selectedCourseUnitIds = new Set<OtmId>();
    private readonly selectedCustomStudyDraftIds = new Set<LocalId>();

    /**
     * The `selectedStudies` form array contains one entry per education phase (top-level module),
     * **in the same order is as emitted by** `educationPhaseStudies$`. The order is important, as
     * the controls are accessed based on their index in the template.
     */
    readonly form: FormGroup<SelectStudiesForm> = this.fb.group({
        selectedStudies: this.fb.array<SelectableStudy[]>([]),
    });

    readonly educationPhaseStudies$ = this.selectableStudiesService.educationPhaseStudies$.pipe(
        // take(1) helps to create a forever-warm cache
        take(1),
        tap(educationPhaseStudies =>
            // Create a new FormControl for each education phase
            educationPhaseStudies.forEach(phase => {
                const initialSelectedOptions: SelectableStudy[] = this.createInitialFormState(phase);
                const control: FormControl<SelectableStudy[] | null> = this.fb.control(initialSelectedOptions);
                this.form.controls.selectedStudies.push(control);

                // save initial selections to the "model"
                this.addToModel(initialSelectedOptions);

                // We want to keep selections synchronized between FormControls (and also within a FormControl),
                // if the same course unit happens to substitute several course units,
                // in which case it can have several SelectableCourseUnit occurrences.
                control.registerOnChange(() => {
                    // First take the changes of this FormControl to the "model"...
                    const modelChanged: boolean = this.updateModel(phase, control);
                    if (!modelChanged) {
                        return;
                    }

                    // ...then (if the model changed) rebuild all FormControl states from the model.
                    this.updateFormStatesFromModel(educationPhaseStudies);
                });
            }),
        ),
        this.appErrorHandler.defaultErrorHandler(),
        // We do NOT want to run the above logic twice.
        // With "take(1)" above, this will create a forever-warm cache during the first subscription, which is fine in a component scope.
        // Notice that ShareReplayConfig.refCount would have no effect here.
        shareReplay(1),
    );

    save(): void {
        handleSubmission(this.form, this.saveQueue);
    }

    private onSuccess(): void {
        this.alertsService.success('TIMELINE.SELECT_COURSE_UNITS.SAVE_SUCCESS');
        this.fudisDialogService.close();
    }

    private saveSelectedStudies(): Observable<void> {
        return this.timingActionService.saveSelectedPlanTimings(
            this.data.studyPeriodData.locator,
            this.selectedCourseUnitIds,
            this.selectedCustomStudyDraftIds,
        );
    }

    private createInitialFormState(phase: EducationPhaseStudies): SelectableStudy[] {
        return phase.optionGroups
            .flatMap(group => group.options)
            .filter(option => option.value.plannedPeriods.includes(this.data.studyPeriodData.locator));
    }

    private createFormStateFromModel(phase: EducationPhaseStudies): SelectableStudy[] {
        return phase.optionGroups
            .flatMap(group => group.options)
            .filter(option => {
                if (isSelectableCourseUnit(option)) {
                    return this.selectedCourseUnitIds.has(option.value.value.id);
                }

                if (isSelectableCustomStudyDraft(option)) {
                    return this.selectedCustomStudyDraftIds.has(option.value.value.id);
                }

                throw new Error(`Option not supported: ${toJson(option)}`);
            });
    }

    /**
     * Returns true if options were actually removed.
     */
    private removeFromModel(options: readonly SelectableStudy[]): boolean {
        let optionsRemoved: boolean = false;

        for (const option of options) {
            if (isSelectableCourseUnit(option)) {
                optionsRemoved ||= this.selectedCourseUnitIds.delete(option.value.value.id);
            } else if (isSelectableCustomStudyDraft(option)) {
                optionsRemoved ||= this.selectedCustomStudyDraftIds.delete(option.value.value.id);
            } else {
                throw new Error(`Option not supported: ${toJson(option)}`);
            }
        }

        return optionsRemoved;
    }

    /**
     * Returns true if options were actually added.
     */
    private addToModel(options: readonly SelectableStudy[]): boolean {
        let optionsAdded: boolean = false;

        for (const option of options) {
            if (isSelectableCourseUnit(option)) {
                const courseUnitId: OtmId = option.value.value.id;
                if (!this.selectedCourseUnitIds.has(courseUnitId)) {
                    this.selectedCourseUnitIds.add(courseUnitId);
                    optionsAdded = true;
                }
            } else if (isSelectableCustomStudyDraft(option)) {
                const customStudyDraftId: LocalId = option.value.value.id;
                if (!this.selectedCustomStudyDraftIds.has(customStudyDraftId)) {
                    this.selectedCustomStudyDraftIds.add(customStudyDraftId);
                    optionsAdded = true;
                }
            } else {
                throw new Error(`Option not supported: ${toJson(option)}`);
            }
        }

        return optionsAdded;
    }

    /**
     * Returns true if the model actually changed.
     */
    private updateModel(phase: EducationPhaseStudies, control: FormControl<SelectableStudy[] | null>): boolean {
        // Find out which exact course unit(s) and custom study draft(s) got added or removed,
        // and update the model accordingly.
        const newSelectedOptions: readonly SelectableStudy[] = getEffectiveSelectedOptions(control);
        const oldSelectedOptions: SelectableStudy[] = this.createFormStateFromModel(phase);
        const optionsRemoved: boolean = this.removeFromModel(
            oldSelectedOptions.filter(oldSelectedOption => newSelectedOptions.indexOf(oldSelectedOption) < 0),
        );
        const optionsAdded: boolean = this.addToModel(
            newSelectedOptions.filter(newSelectedOption => oldSelectedOptions.indexOf(newSelectedOption) < 0),
        );
        return optionsRemoved || optionsAdded;
    }

    private updateFormStatesFromModel(educationPhaseStudies: readonly EducationPhaseStudies[]): void {
        // This will update the originating FormControl's state too (if necessary),
        // because an added/removed course unit can have several options that should be kept in sync.
        for (let i = 0; i < educationPhaseStudies.length; i += 1) {
            const control: FormControl<SelectableStudy[] | null> = this.form.controls.selectedStudies.at(i);

            const phase: EducationPhaseStudies = educationPhaseStudies[i];
            const oldSelectedOptions: readonly SelectableStudy[] = getEffectiveSelectedOptions(control);
            const newSelectedOptions: SelectableStudy[] = this.createFormStateFromModel(phase);

            // Update the FormControl state only when there are actual changes.
            // This actually PREVENTS A CHANGE EVENT LOOP, for FormControl.setValue will dispatch another change event,
            // and none of the option flags seem to prevent this.
            if (!selectedOptionsAreEqual(oldSelectedOptions, newSelectedOptions)) {
                control.setValue(newSelectedOptions);
            }
        }
    }
}

function getEffectiveSelectedOptions(control: FormControl<SelectableStudy[] | null>): readonly SelectableStudy[] {
    return control.getRawValue() ?? [];
}

function selectedOptionsAreEqual(a: readonly SelectableStudy[], b: readonly SelectableStudy[]): boolean {
    return a.length === b.length && a.every((aItem, index) => aItem === b[index]);
}
